import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useGetValueItems } from "../../hooks/values/useGetValueItems";
import { useDeleteValueItem } from "../../hooks/values/useDeleteValueItem";
import { useDeleteAllValueItems } from "../../hooks/values/useDeleteAllValueItems";
import { useRestoreAllValueItems } from "../../hooks/values/useRestoreAllValueItems";
import { useForceDeleteAllValueItems } from "../../hooks/values/useForceDeleteAllValueItems";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import ValuesTableRow from "./Table/ValuesTableRow";
import { ValuesTableColumns } from "./Table/ValuesTableColumns";
import { usePermissions } from "../../auth/PermissionsContext";

const ValuesTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("viewAny-value"),
            create: has("create-value"),
            edit: has("update-value"),
            delete: has("delete-value"),
        }),
        [has]
    );

    const navigate = useNavigate();

    const [trashedFilter, setTrashedFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");

    const { deleteItemMutate, isPending: isDelete } = useDeleteValueItem();
    const { deleteAllItemsMutate, isPending: isDeleteAll } = useDeleteAllValueItems();
    const { restoreAllItemsMutate, isPending: isRestoreAll } = useRestoreAllValueItems();
    const { forceDeleteAllItemsMutate, isPending: isForceDeleteAll } = useForceDeleteAllValueItems();

    const {
        page,
        perPage,
        search,
        order,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handleBulkRestore,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: "/values",
        deleteSingle: deleteItemMutate,
        deleteAll: deleteAllItemsMutate,
        restoreSingle: (id, options) => restoreAllItemsMutate({ items: [id] }, options),
        restoreAll: restoreAllItemsMutate,
        forceDeleteSingle: (id, options) => forceDeleteAllItemsMutate({ items: [id] }, options),
        forceDeleteAll: forceDeleteAllItemsMutate,
        isArchive: trashedFilter === "only",
    });

    const params = useMemo(
        () => ({
            page,
            per_page: perPage,
            search: search || undefined,
            trashed: trashedFilter === "only" ? "only" : undefined,
            is_active: statusFilter || undefined,
            // sort_by: 'sort_order',
            // sort_order: order,
        }),
        [page, perPage, search, trashedFilter, statusFilter]
    );

    const { isLoading, error, items, meta } = useGetValueItems(params);

    const filters = useMemo(() => [
        {
            key: "is_active",
            label: { ar: "الحالة", en: "Status" },
            value: statusFilter,
            onChange: setStatusFilter,
            options: [
                { value: "", label: { ar: "الكل", en: "All" } },
                { value: "1", label: { ar: "نشط", en: "Active" } },
                { value: "0", label: { ar: "غير نشط", en: "Inactive" } },
            ],
        },
    ], [statusFilter]);

    const columns = useMemo(() => ValuesTableColumns(isArabic), [isArabic]);

    return (
        <TableLayout
            title={{ ar: "القيم", en: "Values" }}
            columns={columns}
            items={items}
            meta={meta}
            isLoading={isLoading}
            error={error}
            perms={perms}
            filters={filters}
            selectedIds={selectedIds}
            search={search}
            onSearchChange={setSearch}
            onSelectAllClick={(e) => handleSelectAllClick(e, items)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            onBulkDelete={handleBulkDelete}
            onBulkRestore={handleBulkRestore}
            isBulkDeleting={isDeleteAll || isForceDeleteAll}
            isBulkRestoring={isRestoreAll}
            isArchive={trashedFilter === "only"}
            onAddClick={() => navigate("/values/create")}
            addButtonLabel={{ ar: "إضافة قيمة", en: "Add Value" }}
            page={page}
            perPage={perPage}
            order={order}
            onRequestSort={handleRequestSort}
            menuAnchorEl={menuAnchorEl}
            openMenu={Boolean(menuAnchorEl)}
            onMenuClose={handleMenuClose}
            onMenuEdit={handleEdit}
            onMenuDelete={handleMenuDelete}
            onMenuRestore={handleMenuRestore}
            onArchiveToggle={() => setTrashedFilter(prev => prev === "only" ? "" : "only")}
            isDeletingSingle={isDelete || isForceDeleteAll}
            renderRow={(params) => (
                <ValuesTableRow
                    {...params}
                    key={params.item.id}
                    item={params.item}
                    perms={perms}
                    onMenuOpen={handleMenuOpen}
                />
            )}
        />
    );
};

export default ValuesTable;
